/**
 * useWalmartMonthlyForecast
 *
 * Same logic as useAmazonMonthlyForecast, but Walmart monthly sales are
 * keyed by SKU (walmart_month_sales table) instead of ASIN.
 * Seasonality flags remain ASIN-based (yoy-seasonality-flags).
 *
 * Returns forecastSalesData keyed by SKU: { [sku]: { total_units } }
 */

import { useState, useEffect } from 'react';
import api from '../api/client';
import {
  getCurrentMonthInfo,
  getHistoricalMonths,
  getForecastMonths,
  buildMonthlySalesIndex,
  calculateMonthlyAverageGrowthFromIndex,
  calculateMonthlyForecastFromIndex,
  calculateMonthlySeasonalityForecast,
  buildMonthlyQueryRange,
} from '../utils/yoyMonthlyCalculations';

const DEFAULT_HISTORICAL_MONTHS = 2;

const getHistoricalMonthsSetting = () => {
  try {
    const v = localStorage.getItem('yoy_historical_months_count');
    return v ? Number(v) : DEFAULT_HISTORICAL_MONTHS;
  } catch { return DEFAULT_HISTORICAL_MONTHS; }
};

export const useWalmartMonthlyForecast = (products) => {
  const [forecastSalesData, setForecastSalesData] = useState({});
  const [forecastLoading, setForecastLoading]     = useState(false);

  useEffect(() => {
    if (!products || products.length === 0) return;

    const skus  = products.map(p => p.SKU).filter(Boolean);
    const asins = products.map(p => p.ASIN).filter(Boolean);
    if (skus.length === 0) return;

    let cancelled = false;

    const run = async () => {
      setForecastLoading(true);
      try {
        const monthInfo        = getCurrentMonthInfo();
        const historicalMonths = getHistoricalMonths(monthInfo.month, monthInfo.year, getHistoricalMonthsSetting());
        const forecastMonths   = getForecastMonths(monthInfo.month, monthInfo.year, 0);

        const { startYear, startMonth, endYear, endMonth } = buildMonthlyQueryRange(
          historicalMonths, monthInfo, forecastMonths
        );

        const [salesRes, coeffRes, flagsRes] = await Promise.all([
          api.get(
            `/api/walmart-month-sales-range` +
            `?skus=${skus.join(',')}` +
            `&startMonth=${startMonth}&startYear=${startYear}` +
            `&endMonth=${endMonth}&endYear=${endYear}`
          ),
          api.get(`/api/seasonality-coefficients?marketplace=walmart`),
          asins.length
            ? api.get(`/api/yoy-seasonality-flags?asins=${asins.join(',')}`)
            : Promise.resolve({ data: { data: [] } }),
        ]);

        const salesResult = salesRes.data;
        const coeffResult = coeffRes.data;
        const flagsResult = flagsRes.data;

        if (cancelled || !salesResult.success) return;

        const salesData = {};
        Object.entries(salesResult.data).forEach(([key, value]) => {
          salesData[key] = { value: value.value ?? value.units };
        });

        const coeffMap = {};
        if (coeffResult.success && Array.isArray(coeffResult.data)) {
          coeffResult.data.forEach(item => {
            coeffMap[`${item.niche_category_id}-${item.month_num}`] = parseFloat(item.seasonality_coefficient);
          });
        }
        const getCoeff = (nicheId, month) => coeffMap[`${nicheId}-${month}`] || 1.0;

        const flagsMap = {};
        if (Array.isArray(flagsResult.data)) {
          flagsResult.data.forEach(item => {
            if (item.asin) flagsMap[item.asin.toUpperCase()] = item.seasonality_enabled === 1;
          });
        }

        const map = {};
        products.forEach(product => {
          const sku = product.SKU;
          if (!sku) return;

          const index     = buildMonthlySalesIndex(sku, salesData);
          const avgGrowth = calculateMonthlyAverageGrowthFromIndex(index, historicalMonths);

          let forecast;

          if (product.niche_category_id && product.ASIN && flagsMap[product.ASIN.toUpperCase()]) {
            const seasonal = calculateMonthlySeasonalityForecast(
              sku,
              monthInfo.year,
              monthInfo.month,
              product.niche_category_id,
              index,
              getCoeff,
              monthInfo.month,
              monthInfo.year
            );
            if (seasonal !== 'N/A') forecast = seasonal;
          }

          if (forecast === undefined) {
            const f = calculateMonthlyForecastFromIndex(index, monthInfo.year, monthInfo.month, avgGrowth);
            if (typeof f === 'number') forecast = f;
          }

          if (typeof forecast === 'number') {
            map[sku] = { total_units: forecast };
          }
        });

        setForecastSalesData(map);
      } catch (err) {
        console.error('[useWalmartMonthlyForecast] error:', err);
      } finally {
        if (!cancelled) setForecastLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [products]);

  return { forecastSalesData, forecastLoading };
};
